import { ContactInformation } from './ContactInformation';
import { EntityDetails } from './entity/EntityDetails';
import { CombinedOperationsAndRisk } from './operational/CombinedOperationsAndRisk';
import { PayrollSchedule } from './PayrollSchedule';
import { DocumentUpload } from './DocumentUpload';
import { PEOPreferences } from './PEOPreferences';
import { ReviewAndSubmit } from './ReviewAndSubmit';
import { InsuranceDetails } from './InsuranceDetails';
import { MedicalQuestionnaire } from './insurance/MedicalQuestionnaire';
import { CensusManager } from './census/CensusManager';
import { Card, CardContent } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';
import { useForm } from 'react-hook-form';
import type { Application } from '@/types/database.types';

interface ApplicationSectionProps {
  currentSection: number;
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => Promise<void>;
  onSectionChange?: (section: number) => void;
}

export const ApplicationSection = ({ currentSection, data, onSave, onSectionChange }: ApplicationSectionProps) => {
  const isMobile = useIsMobile();
  const { register, setValue, watch } = useForm<Partial<Application>>({
    defaultValues: data
  });
  
  // Add console logs to debug section rendering
  console.log('Rendering ApplicationSection with section:', currentSection);
  
  // Each section should logically group related fields
  switch (currentSection) {
    // Contact Information (Employee Contact Details)
    case 0:
      return <ContactInformation data={data} onSave={onSave} />;
    
    // Organization Information (Entity Details)
    case 1:
      return <EntityDetails data={data} onSave={onSave} />;
    
    // Insurance Details (Employee Benefits)
    case 2:
      return <InsuranceDetails data={data} onSave={onSave} />;
    
    // Combined Operations & Risk Assessment
    case 3:
      return <CombinedOperationsAndRisk data={data} onSave={onSave} />;
    
    // Medical Questionnaire (Employee Health Assessment)
    case 4:
      return (
        <Card className="border-primary/5 shadow-elevation-1 overflow-hidden">
          <CardContent className="pt-6 space-y-6">
            <div className="flex items-center justify-between gap-3 pb-2 border-b border-gray-100">
              <h2 className="text-2xl font-semibold">Employee Health Assessment</h2>
            </div>
            
            <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 flex items-start gap-3">
              <p className="text-muted-foreground text-sm">
                This information helps us match you with PEO providers that can best meet your organization's health insurance needs.
                All information is kept confidential and only used for coverage evaluation.
              </p>
            </div>
            
            <div className="space-y-8">
              <div className="bg-white p-5 rounded-lg border border-gray-100 shadow-sm">
                <MedicalQuestionnaire 
                  register={register}
                  setValue={setValue}
                  watch={watch}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      );
    
    // Payroll Schedule (Payment Cycles)
    case 5:
      return <PayrollSchedule data={data} onSave={onSave} />;
    
    // Census Information
    case 6:
      return <CensusManager data={data} onSave={onSave} />;
    
    // Document Upload
    case 7:
      return <DocumentUpload data={data} onSave={onSave} />;
    
    // PEO Preferences
    case 8:
      return <PEOPreferences data={data} onSave={onSave} />;
    
    // Review and Submit
    case 9:
      return <ReviewAndSubmit data={data} onSave={onSave} onSectionChange={onSectionChange || (() => {})} />;
    
    default:
      return (
        <div className="text-center text-muted-foreground">
          <h3 className="text-xl font-medium">Section {currentSection + 1}</h3>
          <p className="mt-2">This section is under development</p>
        </div>
      );
  }
};
